// ── Extracted versus HUD rendering functions ────────────────────────

import type { RenderModel } from "../renderModel";
import type { VersusMatchRenderData } from "../../versus/types";
import {
  mFont,
  mFontPlain,
  roundedRectPath,
  resolveMiniDoomSegmentCss,
  resolveMobileVersusStatsRowLayout,
  resolveVersusSuddenDeathBannerState,
  resolveVersusSuddenDeathBannerLayout,
  type EffectiveOpponentDisplayMode,
} from "../renderConstants";
import { VERSUS_MOBILE_CANVAS_WIDTH } from "../../versus/versusConstants";
import { isMobileViewport } from "../../game/platform";

// ── drawMiniHorizontalDoomBar ───────────────────────────────────────

export function drawMiniHorizontalDoomBarFn(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  fillFrac: number,
  warning: boolean,
  mirrored = false
): void {
  // Background
  roundedRectPath(ctx, x, y, w, h, 4);
  ctx.fillStyle = "rgb(20,28,50)";
  ctx.fill();
  roundedRectPath(ctx, x, y, w, h, 4);
  ctx.strokeStyle = warning ? "rgb(255,120,120)" : "rgb(80,100,140)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Block segments
  const blockW = 6;
  const blockGap = 2;
  const innerPad = 3;
  const innerX = x + innerPad;
  const innerY = y + innerPad;
  const innerH = h - innerPad * 2;
  const innerRight = x + w - innerPad;
  const totalInner = innerRight - innerX;
  const totalBlocks = Math.floor(totalInner / (blockW + blockGap));
  const filledBlocks = Math.round(totalBlocks * fillFrac);

  for (let b = 0; b < totalBlocks; b++) {
    const drawIdx = mirrored ? (totalBlocks - 1 - b) : b;
    const bx = innerX + drawIdx * (blockW + blockGap);
    const isFilled = b < filledBlocks;
    const fillOrderFrac = b / Math.max(1, totalBlocks - 1);

    if (isFilled) {
      ctx.fillStyle = resolveMiniDoomSegmentCss(fillOrderFrac);
    } else {
      ctx.fillStyle = "rgba(30,45,70,0.25)";
    }
    roundedRectPath(ctx, bx, innerY, blockW, innerH, 2);
    ctx.fill();
  }
}

// ── drawSyncStatusBadge ─────────────────────────────────────────────

export function drawSyncStatusBadgeScreen(
  ctx: CanvasRenderingContext2D,
  vm: VersusMatchRenderData,
  x: number, y: number
): void {
  const mobile = isMobileViewport();
  const w = mobile ? 142 : 52;
  const h = mobile ? 20 : 18;
  const health = vm.syncHealth;
  const label = health === "HEALTHY"
    ? "GOOD"
    : health === "DEGRADED"
      ? "DEGR"
      : "RECON";
  const fill = health === "HEALTHY"
    ? "rgba(30,72,44,0.9)"
    : health === "DEGRADED"
      ? "rgba(86,58,18,0.92)"
      : "rgba(84,38,38,0.92)";
  const stroke = health === "HEALTHY"
    ? "#79d9a0"
    : health === "DEGRADED"
      ? "#ffc76b"
      : "#ff8e8e";
  roundedRectPath(ctx, x, y, w, h, 4);
  ctx.fillStyle = fill;
  ctx.fill();
  roundedRectPath(ctx, x, y, w, h, 4);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#e8f7ff";
  ctx.textBaseline = "middle";
  if (mobile) {
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "center";
    ctx.fillText(`SYNC ${label}`, x + w / 2, y + h / 2);
  } else {
    ctx.font = mFont("bold", 4);
    ctx.textAlign = "center";
    ctx.fillText(label, x + w / 2, y + h / 2);
  }
}

// ── drawOpponentVisibilityBadge ─────────────────────────────────────

export function drawOpponentVisibilityBadgeScreen(
  ctx: CanvasRenderingContext2D,
  mode: EffectiveOpponentDisplayMode,
  x: number, y: number
): void {
  if (isMobileViewport()) {
    return;
  }
  const w = 86;
  const h = 18;
  const fill = mode.mode === "FULL"
    ? "rgba(24,62,86,0.92)"
    : mode.mode === "ADAPTIVE"
      ? "rgba(78,58,16,0.92)"
      : "rgba(44,50,67,0.92)";
  const stroke = mode.mode === "FULL"
    ? "#76d8ff"
    : mode.mode === "ADAPTIVE"
      ? "#ffd66f"
      : "#b5c4e6";
  const tierWord = mode.mode === "ADAPTIVE"
    ? (mode.adaptiveTier ?? "HEALTHY")
    : mode.mode;
  const label = mode.mode === "ADAPTIVE"
    ? `ADAPT ${tierWord.slice(0, 3)}`
    : mode.mode;

  roundedRectPath(ctx, x, y, w, h, 4);
  ctx.fillStyle = fill;
  ctx.fill();
  roundedRectPath(ctx, x, y, w, h, 4);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "#e8f7ff";
  ctx.font = mFont("bold", 4);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2);
}

// ── drawVersusSuddenDeathBanner ─────────────────────────────────────

export function drawVersusSuddenDeathBannerScreen(
  ctx: CanvasRenderingContext2D,
  vm: VersusMatchRenderData,
  mobile: boolean,
  canvasLogicalWidth: number
): void {
  const banner = resolveVersusSuddenDeathBannerState(vm.suddenDeath);
  if (!banner.visible) {
    return;
  }

  const canvasWidth = mobile ? VERSUS_MOBILE_CANVAS_WIDTH : canvasLogicalWidth;
  const layout = resolveVersusSuddenDeathBannerLayout(mobile, canvasWidth);

  ctx.save();
  roundedRectPath(ctx, layout.panel.x, layout.panel.y, layout.panel.w, layout.panel.h, 8);
  ctx.fillStyle = "rgba(40,8,12,0.92)";
  ctx.fill();
  roundedRectPath(ctx, layout.panel.x, layout.panel.y, layout.panel.w, layout.panel.h, 8);
  ctx.strokeStyle = "rgba(255,120,80,0.92)";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffd7a8";
  ctx.font = mFont("bold", mobile ? 7 : 6);
  ctx.fillText(banner.headline, layout.panel.x + layout.panel.w / 2, layout.headlineY);

  ctx.fillStyle = "#fff2cf";
  ctx.font = mFont("bold", mobile ? 16 : 18);
  ctx.fillText(banner.countdownText, layout.panel.x + layout.panel.w / 2, layout.countdownY);

  ctx.fillStyle = "#ff9e7a";
  ctx.font = mFont("bold", mobile ? 6 : 5);
  ctx.fillText(banner.targetText, layout.panel.x + layout.panel.w / 2, layout.targetY);
  ctx.restore();
}

// ── drawVersusBottomStats ───────────────────────────────────────────

export function drawVersusBottomStatsScreen(
  ctx: CanvasRenderingContext2D,
  model: RenderModel,
  vm: VersusMatchRenderData,
  leftStatsX: number,
  rightStatsX: number,
  characterBaseY: number,
  drawStarIcon: (x: number, y: number, size: number) => void
): void {
  // Position at bottom near portraits
  const statsY = characterBaseY + 108;
  const midX = Math.floor((leftStatsX + rightStatsX) / 2);
  const barGap = 4; // gap between the two doom bars in the center
  const doomBarH = 16;
  const starSize = 10;
  const starGap = 2;

  // -- Left side: local player --
  {
    const playerName = (model.username ?? "PLAYER").toUpperCase().slice(0, 12);
    const playerScore = String(Math.floor(model.displayScore));
    const playerDoomPct = model.doomClockMaxSec > 0
      ? Math.min(1, model.doomClockSec / model.doomClockMaxSec) : 0;

    ctx.save();
    ctx.shadowColor = "#000";
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.shadowBlur = 2;

    ctx.textAlign = "left";
    ctx.fillStyle = "#c8dbf0";
    ctx.font = mFont("bold", 6);
    ctx.textBaseline = "top";
    ctx.fillText(playerName, leftStatsX, statsY);

    // Star icon + score
    const scoreY = statsY + 12;
    const starY = scoreY - 2;
    drawStarIcon(leftStatsX, starY, starSize);
    ctx.fillStyle = "#fff14f";
    ctx.font = mFontPlain(6);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(playerScore, leftStatsX + starSize + starGap, scoreY);
    ctx.restore();

    // Doom bar fills from left edge to center
    const leftBarW = midX - barGap / 2 - leftStatsX;
    const leftBarY = scoreY + 14;
    drawMiniHorizontalDoomBarFn(ctx, leftStatsX, leftBarY, leftBarW, doomBarH, playerDoomPct, false, false);
  }

  // -- Right side: opponent --
  const opName = vm.opponentStats.displayName.toUpperCase().slice(0, 12);
  const opScore = String(Math.floor(vm.opponentStats.score));
  const opDoomPct = 1 - Math.min(1, Math.max(0, vm.opponentStats.doomPct));

  ctx.save();
  ctx.shadowColor = "#000";
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.shadowBlur = 2;

  ctx.textAlign = "right";
  ctx.textBaseline = "top";

  ctx.fillStyle = "#c8dbf0";
  ctx.font = mFont("bold", 6);
  ctx.fillText(opName, rightStatsX, statsY);

  // Star icon + score (right-aligned: score then star)
  const opScoreY = statsY + 12;
  ctx.font = mFontPlain(6);
  ctx.fillStyle = "#fff14f";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  const opScoreW = ctx.measureText(opScore).width;
  const opStarX = rightStatsX - opScoreW - starGap - starSize;
  const opStarY = opScoreY - 2;
  drawStarIcon(opStarX, opStarY, starSize);
  ctx.fillText(opScore, rightStatsX, opScoreY);
  ctx.restore();

  // Doom bar fills from center to right edge
  const rightBarX = midX + barGap / 2;
  const rightBarW = rightStatsX - rightBarX;
  const rightBarY = opScoreY + 14;
  drawMiniHorizontalDoomBarFn(ctx, rightBarX, rightBarY, rightBarW, doomBarH, opDoomPct, false, true);

  ctx.textAlign = "left";
}

// ── drawMobileVersusStatsRows ───────────────────────────────────────

export function drawMobileVersusStatsRowsScreen(
  ctx: CanvasRenderingContext2D,
  model: RenderModel,
  vm: VersusMatchRenderData,
  baseY: number,
  drawStarIcon: (x: number, y: number, size: number) => void,
  drawCashIcon: (x: number, y: number, size: number) => void
): void {
  const canvasW = VERSUS_MOBILE_CANVAS_WIDTH;
  const layout = resolveMobileVersusStatsRowLayout(baseY, canvasW);
  const playerName = (model.username ?? "PLAYER").toUpperCase().slice(0, 12);
  const playerScore = String(Math.floor(model.displayScore));
  const playerCash = String(Math.max(0, Math.floor(model.run?.cashBalance ?? 0)));
  const playerDoomPct = model.doomClockMaxSec > 0
    ? Math.min(1, model.doomClockSec / model.doomClockMaxSec) : 0;
  const iconSize = 14;
  const iconGap = 1;
  const statGap = 6; // gap between score and cash
  const fontPx = 6;
  // Align icon center with text midline (nudge down 1px for visual balance)
  const iconY = (sz: number) => layout.scoreY + Math.round((fontPx - sz) / 2) + 1;

  // -- Left side: local player --
  ctx.save();
  ctx.shadowColor = "#000";
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.shadowBlur = 2;

  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  ctx.fillStyle = "#c8dbf0";
  ctx.font = mFont("bold", 7);
  ctx.fillText(playerName, layout.leftX, layout.statsY);

  // Star icon + score + cash icon + cash (left-aligned, dynamic spacing)
  let curX = layout.leftX;
  drawStarIcon(curX, iconY(iconSize), iconSize);
  curX += iconSize + iconGap;
  ctx.fillStyle = "#fff14f";
  ctx.font = mFontPlain(fontPx);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(playerScore, curX, layout.scoreY);
  curX += ctx.measureText(playerScore).width + statGap;
  drawCashIcon(curX, iconY(iconSize), iconSize);
  curX += iconSize + iconGap;
  ctx.fillStyle = "#9fffa4";
  ctx.fillText(playerCash, curX, layout.scoreY);
  ctx.restore();

  drawMiniHorizontalDoomBarFn(ctx, layout.playerBarX, layout.barY, layout.doomBarW, layout.doomBarH, playerDoomPct, false, false);

  // -- Right side: opponent --
  const opName = vm.opponentStats.displayName.toUpperCase().slice(0, 12);
  const opScore = String(Math.floor(vm.opponentStats.score));
  const opCash = String(Math.max(0, Math.floor(vm.opponentStats.cash ?? 0)));
  const opDoomPct = 1 - Math.min(1, Math.max(0, vm.opponentStats.doomPct));

  ctx.save();
  ctx.shadowColor = "#000";
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.shadowBlur = 2;

  ctx.textAlign = "right";
  ctx.textBaseline = "top";

  ctx.fillStyle = "#c8dbf0";
  ctx.font = mFont("bold", 7);
  ctx.fillText(opName, layout.rightX, layout.statsY);

  // Cash icon + cash + star icon + score (right-aligned, dynamic spacing)
  ctx.font = mFontPlain(fontPx);
  ctx.textBaseline = "top";
  let opCurX = layout.rightX;
  // Score first (rightmost)
  ctx.fillStyle = "#fff14f";
  ctx.textAlign = "right";
  ctx.fillText(opScore, opCurX, layout.scoreY);
  opCurX -= ctx.measureText(opScore).width + iconGap;
  drawStarIcon(opCurX - iconSize, iconY(iconSize), iconSize);
  opCurX -= iconSize + statGap;
  // Cash
  ctx.fillStyle = "#9fffa4";
  ctx.textAlign = "right";
  ctx.fillText(opCash, opCurX, layout.scoreY);
  opCurX -= ctx.measureText(opCash).width + iconGap;
  drawCashIcon(opCurX - iconSize, iconY(iconSize), iconSize);
  ctx.restore();

  drawMiniHorizontalDoomBarFn(ctx, layout.opponentBarX, layout.barY, layout.doomBarW, layout.doomBarH, opDoomPct, false, true);

  ctx.textAlign = "left";
}
